import { ThemeOptions } from '@mui/material';

// Dark mode palette constants
export const darkPaletteColors = {
    // Primary colors
    primary: {
        main: '#F17A20',
        hover: '#F38F45',
        contrastText: '#E6EDF3',
        divider: '#212B36',
    },

    // Secondary colors
    secondary: {
        main: '#95E347',
        contrastText: '#0D1117',
    },

    // Background colors
    background: {
        default: '#0D1117', // Dark blue-black background
        paper: '#161B22', // Slightly lighter than default for UI layers
        divider: '#212B36',
    },

    // Text colors
    text: {
        primary: '#E6EDF3', // Soft white
        secondary: '#9BA3B0', // Light gray, reduced glare
        disabled: '#6E7681', // Muted gray
    },

    // Semantic colors
    success: {
        main: '#28A745',
    },
    warning: {
        main: '#F5A623',
    },
    error: {
        main: '#fc0362',
    },
    info: {
        main: '#03a5fc',
    },

    // Gray scale
    grey: {
        50: '#F9FAFB',
        100: '#F4F6F8',
        200: '#DFE3E8',
        300: '#C4CDD5',
        400: '#919EAB',
        500: '#637381',
        600: '#454F5B',
        700: '#212B36',
        800: '#161C24',
        850: '#0F1419',
        900: '#0D1117',
    },
};

// Dark mode UI colors
export const darkUiColors = {
    primaryButton: '#F17A20',
    secondaryButton: '#95E347',
    accentHover: '#F38F45',
    background: '#0D1117',
    textPrimary: '#E6EDF3',
    textSecondary: '#9BA3B0',
    gradientStart: '#F17A20',
    gradientEnd: '#95E347',
    link: '#35B7FC',
    linkHover: '#03a5fc',
};

// Keep original VMB Education gradient colors for landing page navigation buttons only
export const landingGradientColors = {
    vividGreen: '#7ED321',
    brightBlue: '#4A90E2',
    deepBlue: '#2E5BBA',
    lightSkyBlue: '#87CEEB',
    white: '#FFFFFF',
};

// Extended color palette for dark mode
export const darkColors = {
    ...darkUiColors,

    // Semantic colors
    primary: darkPaletteColors.primary.main,
    success: darkPaletteColors.success.main,
    warning: darkPaletteColors.warning.main,
    error: darkPaletteColors.error.main,
    info: darkPaletteColors.info.main,

    // Neutral colors
    grey: darkPaletteColors.grey,

    // Text colors
    text: darkPaletteColors.text,

    gradients: {
        primary: `linear-gradient(135deg, ${darkUiColors.primaryButton} 0%, ${darkUiColors.secondaryButton} 100%)`,
        primaryHover: `linear-gradient(135deg, ${darkUiColors.primaryButton} 0%, ${darkUiColors.secondaryButton} 100%)`,
        secondary: darkUiColors.secondaryButton,
        success: darkPaletteColors.success.main,
        warning: darkPaletteColors.warning.main,
        error: darkPaletteColors.error.main,
        info: darkPaletteColors.info.main,
        background: darkPaletteColors.background.default,
        card: darkPaletteColors.background.paper,
    },

    blue: {
        300: '#5DADE2',
    },

    lightGreen: {
        300: '#48D1CC',
    },

    green: {
        300: '#52D17C',
    },

    purple: {
        300: '#BB6BD9',
    },

    red: {
        200: '#EC7063',
    },
};

const isVietnamese = (language?: string) => {
    const currentLang =
        language || (typeof window !== 'undefined' && window.localStorage ? localStorage.getItem('i18nextLng') : '');
    return currentLang === 'vi' || currentLang === 'vi-VN';
};

const getFontFamily = (defaultFont: string, vietnameseFont: string, language?: string) => {
    return isVietnamese(language) ? vietnameseFont : defaultFont;
};

export const darkThemeOptions = (language?: string): ThemeOptions => {
    return {
        breakpoints: {
            values: {
                xs: 0,
                sm: 600,
                md: 900,
                lg: 1200,
                xl: 1536,
                xxl: 1920,
            },
        },

        palette: {
            mode: 'dark',
            primary: darkPaletteColors.primary,
            secondary: darkPaletteColors.secondary,
            background: darkPaletteColors.background,
            text: darkPaletteColors.text,
            success: darkPaletteColors.success,
            warning: darkPaletteColors.warning,
            error: darkPaletteColors.error,
            info: darkPaletteColors.info,
            grey: darkPaletteColors.grey,
            divider: darkPaletteColors.primary.divider,
        },

        typography: {
            fontFamily: [
                'IBM Plex Sans',
                'Open Sans',
                'Noto Sans',
                '-apple-system',
                'BlinkMacSystemFont',
                'Segoe UI',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
            ].join(','),
            fontSize: 16,
            h1: {
                fontFamily: getFontFamily('Poppins', 'Open Sans', language),
                fontSize: '3.5rem',
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
            },
            h2: {
                fontFamily: getFontFamily('Poppins', 'Open Sans', language),
                fontSize: '2.5rem',
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
            },
            h3: {
                fontFamily: getFontFamily('Poppins', 'Open Sans', language),
                fontSize: '2.25rem',
                fontWeight: 600,
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
            },
            h4: {
                fontFamily: getFontFamily('Poppins', 'Open Sans', language),
                fontSize: '1.875rem',
                fontWeight: 600,
                lineHeight: 1.4,
            },
            h5: {
                fontFamily: getFontFamily('Poppins', 'Open Sans', language),
                fontSize: '1.5rem',
                fontWeight: 600,
                lineHeight: 1.4,
            },
            h6: {
                fontFamily: getFontFamily('Poppins', 'Open Sans', language),
                fontSize: '1rem',
                fontWeight: 600,
                lineHeight: 1.4,
            },
            body1: {
                fontSize: '1.125rem',
                lineHeight: 1.6,
                fontWeight: 400,
            },
            body2: {
                fontSize: '1rem',
                lineHeight: 1.5,
                fontWeight: 400,
            },
            subtitle1: {
                fontFamily: getFontFamily('Sarabun', 'Noto Sans', language),
                fontSize: '1.25rem',
                lineHeight: 1.5,
                fontWeight: 500,
            },
            subtitle2: {
                fontFamily: getFontFamily('Sarabun', 'Noto Sans', language),
                fontSize: '1.125rem',
                lineHeight: 1.5,
                fontWeight: 500,
            },
            menu: {
                fontFamily: getFontFamily('Sarabun', 'Noto Sans', language),
                fontSize: '1.125rem',
                lineHeight: 1.2,
                fontWeight: 400,
            },
        },

        shape: {
            borderRadius: 8,
        },

        spacing: 8,

        gradients: {
            primary: darkColors.gradients.primary,
            secondary: darkColors.secondaryButton,
            success: darkColors.success,
            background: darkColors.background,
            card: darkColors.gradients.card,
            button: darkColors.gradients.primary,
            buttonHover: darkColors.gradients.primaryHover,
        },

        landingGradients: {
            primary: `linear-gradient(135deg, ${darkUiColors.primaryButton} 0%, ${darkUiColors.secondaryButton} 100%)`,
            hover: `linear-gradient(135deg, ${darkUiColors.primaryButton} 0%, ${darkUiColors.secondaryButton} 100%)`,
            text: `linear-gradient(135deg, ${darkUiColors.primaryButton} 0%, ${darkUiColors.secondaryButton} 100%)`,
        },

        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        borderRadius: 50,
                        fontWeight: 600,
                        fontSize: '1.125rem',
                        padding: '8px 16px',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                        },
                    },
                    contained: {
                        background: darkColors.primary,
                    },
                    outlined: {
                        background: darkPaletteColors.background.paper,
                        borderColor: darkPaletteColors.grey[600],
                        color: darkColors.primary,
                        borderWidth: 2,
                    },
                },
                variants: [
                    {
                        props: { variant: 'link' },
                        style: {
                            borderColor: darkUiColors.link,
                            color: darkUiColors.link,
                            borderWidth: 2,
                            boxShadow: 'none',
                            fontSize: '0.875rem',
                            '&:hover': {
                                borderColor: 'unset',
                                color: darkUiColors.linkHover,
                                backgroundColor: 'transparent',
                                borderWidth: 0,
                                boxShadow: 'none',
                            },
                        },
                    },
                    {
                        props: { variant: 'noOutlined' },
                        style: {
                            borderColor: 'unset',
                            color: darkUiColors.primaryButton,
                            borderWidth: 0,
                            boxShadow: 'none',
                            '&:hover': {
                                borderColor: darkUiColors.accentHover,
                                backgroundColor: 'rgba(241, 122, 32, 0.08)',
                                borderWidth: 2,
                            },
                        },
                    },
                ],
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        transition: 'all 0.3s ease',
                        border: `1px solid ${darkPaletteColors.grey[700]}`,
                        backgroundColor: darkPaletteColors.background.paper,
                        '&:hover': {
                            transform: 'translateY(-6px)',
                            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
                        },
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        height: 8,
                        background: darkPaletteColors.grey[700],
                    },
                    bar: {
                        borderRadius: 10,
                        background: `linear-gradient(90deg, ${darkUiColors.primaryButton} 0%, ${darkUiColors.secondaryButton} 100%)`,
                    },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    h1: {
                        background: `linear-gradient(135deg, ${darkUiColors.primaryButton} 0%, ${darkUiColors.secondaryButton} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                    },
                },
            },
        },
    };
};
